"use client"

/**
 * 06 — Get started with your baseline. Figma 314:10569.
 *
 * A 1286×671 card, radius 32, centred on the PAGE rather than on the content
 * column (the frame puts it at x=113 of 1512), over a photographic ground that
 * bleeds past it. Every offset below was measured off the node tree, so the
 * numbers in the JSX are the design's own, in card-local coordinates:
 *
 *   left column   128,102  374 wide   heading 48/56 · 16 · lede 18/24
 *   price         128,308  the membership figure Medium 40 + its period Light 24
 *   button        128,385  347×52, radius 100, white, 32px side padding
 *   right column  692,116  485 wide, 24 between the promise row and the list
 *   promise cols  146 wide, 40px icon box, 28px icon, label 14/20 Medium
 *   list rows     319×36, gap 4, check 22, text 16/20
 *   upsell strip  126,505  1049×103, radius 18
 *
 * Both columns end at y=437, which is why the strip hangs off a single margin
 * rather than off whichever column happens to be taller.
 */

import { blobMediaUrl } from "@/lib/blob-url"
import { useEffect, useRef, useState, type ReactNode } from "react"
import Image from "next/image"
import { useCountry } from "@/components/layout/country-provider"
import { useLivePrices } from "@/lib/use-live-prices"
import { formatCurrency } from "@/lib/format"
import { scanOnTopOfPlan } from "@/lib/pricing/member-prices"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/lib/intl/use-translations"
import { CheckCircleNeutralIcon } from "@/components/ui/icons"
import {
  ShieldPlusIcon,
  TargetIcon,
  StethoscopeFigmaIcon,
  PercentSealIcon,
} from "./home-v4-figma"
import { useHomeV4Copy } from "./home-v4-copy"
import { OneTimeLink, PlanPricesProvider } from "../pricing/pricing-v5-shared"
import { usePricingV5Copy } from "../pricing/pricing-v5-copy"
import { PlanInfoSheet } from "../pricing/pricing-v5-plan-card"
import { MriInfoSheet } from "../pricing/pricing-v5-mri-cards"
import type { PricingV5Data } from "../pricing/pricing-v5-page-data"
import { membershipAnnualAmount, bookingFlowUrl } from "./home-v4-data"

/** The one scan the card offers: the plain full-body MRI (Eugene, 13.10). */
type MriChoice = "core"

const DEV = blobMediaUrl("/images/marketing/home-v3")

/**
 * A figure that rolls to its new value instead of cutting to it — the price
 * is the one number on this card people are deciding on, and a silent swap
 * reads as a re-render rather than as "this is what adding the MRI costs".
 *
 * It counts the amount and re-formats every frame, so grouping and the
 * currency code stay the market's own rather than being spliced together from
 * characters. Reduced motion gets the destination immediately.
 */
function RollingPrice({
  amount,
  country,
  className,
}: {
  amount: number
  country: string
  className?: string
}) {
  const [shown, setShown] = useState(amount)
  const fromRef = useRef(amount)
  useEffect(() => {
    const from = fromRef.current
    if (from === amount) return
    // Jump rather than roll when the animation cannot actually run: reduced
    // motion, or a document that is not visible — a backgrounded tab freezes
    // rAF, and a price frozen half-way through a roll is a wrong price.
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      document.visibilityState !== "visible"
    ) {
      fromRef.current = amount
      setShown(amount)
      return
    }
    const start = performance.now()
    const DURATION = 260
    let raf = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION)
      // ease-out-expo: fast off the mark, settles rather than stops dead
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
      setShown(Math.round(from + (amount - from) * eased))
      if (t < 1) raf = requestAnimationFrame(tick)
      else fromRef.current = amount
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      // Whatever interrupted the roll, the figure must end up at the real
      // amount rather than wherever the last frame left it.
      fromRef.current = amount
      setShown(amount)
    }
  }, [amount])
  // `tabular-nums` so the digits do not jostle sideways while they run. The
  // box is left at its natural width: holding it open at the widest figure
  // the slot can take put a hole between the short price and its note
  // (Eugene, 08.09). The note therefore moves once, early in the roll, when
  // the figure gains its thousands digit, and then stands still.
  return (
    <span className={cn("tabular-nums", className)}>
      {formatCurrency(shown, country)}
    </span>
  )
}

/**
 * Copy that changes when the MRI goes on or off. The card already rolls its
 * price rather than cutting to it, and text that cut while the figure rolled
 * read as two different cards (Eugene, 08.09).
 *
 * A dissolve and nothing else: both versions occupy the same grid cell and
 * fade through each other. The first pass rolled the words up out of masks
 * one at a time, which read as playful and abrupt for a price card; the
 * second added a blur and a few pixels of drift to each layer, and animated
 * blur on text over a photograph is what Eugene saw as a shimmer (08.09).
 * Opacity alone: nothing moves, nothing re-rasterises.
 *
 * `render` rather than children, so the outgoing layer can be drawn from the
 * previous token instead of a copy of stale JSX kept in state. It is dropped
 * when its own animation ends, so nothing dead is left in the tree.
 */
function CrossFade({
  as: Tag,
  token,
  render,
  className,
}: {
  as: "h2" | "p"
  token: string
  render: (token: string) => ReactNode
  className?: string
}) {
  const [shown, setShown] = useState(token)
  // The version on its way out. Null except during a swap, so the common case
  // is a single layer with no animation running at all.
  const [leaving, setLeaving] = useState<string | null>(null)

  useEffect(() => {
    if (shown === token) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(token)
      setLeaving(null)
      return
    }
    setLeaving(shown)
    setShown(token)
    // The outgoing layer takes itself out on `animationend` rather than on a
    // timer: `shown` is a dependency here, so setting it re-runs this effect
    // and a cleanup would clear the very timer that was meant to fire.
  }, [token, shown])

  return (
    <Tag className={cn("hv3-bl-swap", className)}>
      {/* Keyed by the token, so a new version is a new element and its entry
          animation starts from the beginning rather than being inherited. */}
      <span key={shown} className={cn(leaving && "hv3-bl-swap-in")}>
        {render(shown)}
      </span>
      {leaving ? (
        <span
          key={`${leaving}-out`}
          className="hv3-bl-swap-out"
          aria-hidden
          onAnimationEnd={() => setLeaving(null)}
        >
          {render(leaving)}
        </span>
      ) : null}
    </Tag>
  )
}

const PROMISE_ICONS = {
  shield: ShieldPlusIcon,
  target: TargetIcon,
  stethoscope: StethoscopeFigmaIcon,
}

/**
 * The card's own rungs. None of these are on the DS ladder and none should be:
 * the price pair is a figure, not body copy, and Aeonik Light (300) exists in
 * the site's @font-face set but has no token at all. Scoped to this card by
 * class name so nothing else can pick them up.
 *
 * Whatever stands after the figure — the bare period, or the note the MRI
 * puts there — is one treatment in both states: Figma's 24px full-white
 * period next to the MRI's 16px at 70% meant the type and the colour after
 * the slash changed as you flicked the switch (Eugene, 08.09).
 */
function BaselineType() {
  return (
    <style>{`
      .hv3-bl-mri-switch{box-shadow:inset 0 0 0 1px rgba(255,255,255,.4)} /* ds-color-ignore — white glass track */
      .hv3-bl-mri-switch[aria-checked="true"]{background:#65b957;box-shadow:none}
      .hv3-bl-mri-switch[aria-checked="true"] .hv3-bl-knob{background:#f3faef}
      .hv3-bl-mri-switch:hover{filter:brightness(1.08)}
      .hv3-bl-price{font-size:40px;line-height:40px;font-weight:500}
      .hv3-bl-upsell-title{font-size:20px;line-height:32.4px;letter-spacing:-.2592px;font-weight:500}
      .hv3-bl-upsell-price{font-size:20px;line-height:40px;font-weight:500}
      .hv3-bl-upsell-suffix{font-size:16px;line-height:40px;font-weight:300}
      .hv3-bl-sub{font-size:16px;line-height:19.6px;font-weight:400}
      /* Lifted off the photograph: the file's rgba(11,14,21,.5) ground and a
         neutral-900 hairline all but vanished on the dark still, so the one
         real choice in the section was the hardest thing to see (Eugene,
         15.09). Now a pale frosted glass with a white hairline; a touch more
         of both on hover, and a green edge once the MRI is on. */
      .hv3-bl-strip{background-color:rgba(255,255,255,.09);box-shadow:inset 0 0 0 1px rgba(255,255,255,.22)} /* ds-color-ignore — white glass over a photograph */
      .hv3-bl-strip:hover{background-color:rgba(255,255,255,.13);box-shadow:inset 0 0 0 1px rgba(255,255,255,.34)} /* ds-color-ignore */
      .hv3-bl-strip[data-on]{box-shadow:inset 0 0 0 1.5px var(--color-deep-green-400)}
      .hv3-bl-knob{background-color:#fff} /* ds-color-ignore — off: a white knob on a glass track. The file's near-black knob on a white track read as a stray dot on the new blue card (Eugene, 12.10). */

      /* The heading's swap. Both lines share one grid cell, so they dissolve
         through each other instead of one waiting for the other, and the
         column below never moves. The outgoing layer is inert: it is a copy
         on its way out, not something to hit-test or read out. */
      .hv3-bl-swap{display:grid}
      .hv3-bl-swap>*{grid-area:1/1}
      .hv3-bl-swap-out{pointer-events:none;animation:hv3-bl-out 300ms cubic-bezier(.4,0,1,1) both}
      .hv3-bl-swap-in{animation:hv3-bl-in 440ms cubic-bezier(0,0,.2,1) backwards}

      /* Both notes in one slot, stacked and baseline-aligned, so the slot is
         as wide and as tall as the longer of them and only the opacity
         changes. Nothing is set after it, so the slack is invisible. */
      .hv3-bl-fade{display:inline-grid;align-items:baseline}
      .hv3-bl-fade>*{grid-area:1/1}
      .hv3-bl-fade>*{opacity:0;transition:opacity 320ms cubic-bezier(0,0,.2,1)}
      .hv3-bl-fade>.hv3-bl-fade-on{opacity:1}
      /* On phones the hidden note leaves the flow, so the long MRI note no
         longer widens the slot and pushes "/year" under the price
         (Eugene, 06.10). */
      @media (max-width:767px){.hv3-bl-fade{position:relative}.hv3-bl-fade>:not(.hv3-bl-fade-on){position:absolute;left:0;white-space:nowrap}}
      @media (prefers-reduced-motion: reduce){.hv3-bl-fade>*{transition:none}}
      @keyframes hv3-bl-out{from{opacity:1}to{opacity:0}}
      @keyframes hv3-bl-in{from{opacity:0}to{opacity:1}}
      @media (prefers-reduced-motion: reduce){
        .hv3-bl-swap-in,.hv3-bl-swap-out{animation:none}
        .hv3-bl-swap-out{display:none}
      }
    `}</style>
  )
}

export function HomeV4Baseline({
  section,
  planData,
}: {
  section: string
  /** Prices and biomarkers for the "Learn more" popup, /pricing's own. */
  planData: PricingV5Data
}) {
  const { BASELINE, membershipPeriod } = useHomeV4Copy()
  const { PLAN } = usePricingV5Copy()
  const { code: country } = useCountry()
  // The MRI figure is a real Stripe price; `lint:no-hardcoded-prices` blocks a
  // literal and the fallback→live swap keeps the currency right per market.
  //
  // WHAT THE + MEANS. The figure is what a member pays on top of the
  // membership, once. The plain full-body MRI holds no blood test, so that is
  // its whole member price (1'890), not a difference as Advanced's 1'991 was.
  // Member keys only come with the live feed, so the figure stays empty until
  // it lands.
  //
  // CAUTION: the membership-first booking app does not sell Core yet, and
  // `withMriPackage` (lib/booking.ts) books Advanced in its place, so the flow
  // shows 1'991 after this card said 1'890 (13.10).
  const prices = useLivePrices(country)
  // The membership's own yearly price is Stripe's too, read from the prices
  // the server fetched for the page so the first paint and hydration agree.
  const planAmount = membershipAnnualAmount(planData.initialPrices?.prices)
  const planPrice = formatCurrency(planAmount, country)
  const onTop = (key: MriChoice) =>
    scanOnTopOfPlan(prices.prices, key, planAmount) ?? 0
  const [mri, setMri] = useState<MriChoice | null>(null)
  const withMri = mri !== null
  const mriAmount = mri ? onTop(mri) : 0
  // With the MRI on, the figure is what you pay today — membership plus the
  // one-off scan — and the yearly part becomes the note beside it.
  const total = planAmount + mriAmount
  // The MRI add-on is a real choice, not a picture of one: it changes the
  // price line and the sentence above it, and Book carries it into the
  // booking app, which opens the plan with the Full-body MRI attached.
  const toggle = (id: MriChoice) => setMri((v) => (v === id ? null : id))
  const [infoOpen, setInfoOpen] = useState(false)
  const [mriInfoOpen, setMriInfoOpen] = useState(false)
  const learnMore = useTranslations("ui")("learnMore")

  // Switching the MRI on adds it to what the membership includes, second in
  // the list, beside the blood test it extends. The row is always in the tree
  // and opens from zero height, so the rows under it slide down instead of
  // being shunted (Eugene, 08.09).
  const includedRows = [
    { item: BASELINE.included[0], open: true },
    { item: BASELINE.includedMri, open: withMri },
    ...BASELINE.included.slice(1).map((item) => ({ item, open: true })),
  ]

  return (
    <PlanPricesProvider data={planData}>
      <section id="baseline-background-study" className="bg-white">
        <BaselineType />
        <div className={section}>
          {/* Two layers on purpose: percentage padding resolves against the
            CONTAINING block, so on the card itself 9.95% was 9.95% of the
            1360px content column (135px), not of the 1286px card (128px). The
            outer div only sets the width; the inner one carries the padding
            and so measures its percentages against the card. */}
          <div className="mx-auto max-w-[1286px]">
            <div className="background-study-card relative isolate overflow-hidden rounded-[32px] px-4 py-10 md:p-10 md:pt-[80px] md:pr-[8.48%] md:pb-[80px] md:pl-[9.95%]">
              {/* Figma 314:11672 — a 1535×864 image hung at (-207,-90), i.e. it
              overflows the card on every side and the card clips it. */}
              <Image
                src={`${DEV}/baseline-bg.jpg`}
                alt=""
                fill
                sizes="(min-width: 1286px) 1286px, 100vw"
                className="background-study-original -z-10 object-cover"
              />
              <div className="background-study-photo" aria-hidden="true" />

              {/* Mobile stacks in DOM order — copy, then the add-on strip, then
              the features — so switching the MRI on shows its effect right
              under the button instead of a screen further down. From `lg` the
              same three blocks go back on the Figma grid by explicit
              placement: copy left, features right, strip across the foot.

              The columns are minmax(0, design width), not fixed: at the
              design's 1286 they are exactly 374 and 485, and as the card
              narrows they give up width together instead of holding their
              size until they collide. With the side padding shrinking first
              and the button no longer pinning the left column at 347, two
              columns hold from `md` — Eugene twice caught the card stacking
              while there was still room. */}
              <div className="flex flex-col gap-8 md:grid md:grid-cols-[minmax(0,440px)_minmax(0,485px)] md:grid-rows-[auto_auto] md:justify-between md:gap-x-10 md:gap-y-0 lg:grid-cols-[440px_minmax(0,485px)]">
                {/* 440, not the frame's 374: "Start with a baseline blood test"
                  broke over three lines at 374 (Eugene, 30.09). The lede
                  keeps the frame's measure. */}
                <div className="order-1 md:order-none md:col-start-1 md:row-start-1 md:max-w-[440px]">
                  {/* The heading stays on the check-up with the MRI on too;
                    only the line under it changes (Eugene, 15.10). The same
                    type and padding on a phone as /pricing's plan card, so
                    the two read as one card (Eugene, 14.10). */}
                  <h2 className="type-display-md-emphasized text-balance text-white">
                    {BASELINE.heading}
                  </h2>
                  <CrossFade
                    as="p"
                    token={withMri ? "mri" : "base"}
                    className="type-body-xl mt-6 min-h-[72px] text-white md:mt-4 md:max-w-[374px]"
                    render={(t) =>
                      /* With the MRI on: "Your first check-up, the full-body
                       MRI, and your doctor's plan." The commas belong to the
                       insert. */
                      t === "mri" ? (
                        <>
                          {BASELINE.ledeMri.before}
                          <span className="font-medium">
                            , {BASELINE.upsell.ledeInsert},
                          </span>{" "}
                          {BASELINE.ledeMri.after}
                        </>
                      ) : (
                        BASELINE.lede
                      )
                    }
                  />
                  {/* 30px below the heading block, 37 above the button. */}
                  <p className="mt-2 flex min-h-10 flex-wrap items-baseline text-white md:mt-[6px] lg:h-10 lg:flex-nowrap lg:whitespace-nowrap">
                    <RollingPrice
                      amount={withMri ? total : planAmount}
                      country={country}
                      className="hv3-bl-price"
                    />
                    {/* Both notes are always in the tree and only fade past each
                      other: mounting one as the other unmounts made the line
                      re-flow on the frame the figure was still rolling. */}
                    <span className="hv3-bl-fade">
                      <span
                        className={cn(
                          "hv3-bl-upsell-suffix pl-2 text-white/70",
                          !withMri && "hv3-bl-fade-on"
                        )}
                        aria-hidden={withMri}
                      >
                        {membershipPeriod}
                      </span>
                      <span
                        className={cn(
                          "hv3-bl-upsell-suffix pl-2 text-white/70 max-md:pl-0",
                          withMri && "hv3-bl-fade-on"
                        )}
                        aria-hidden={!withMri}
                      >
                        {BASELINE.upsell.recurringNote(
                          planPrice,
                          membershipPeriod
                        )}
                      </span>
                    </span>
                  </p>
                  {/* The switch carries into the flow: with the MRI on, the
                    plan opens with the Full-body MRI attached, the scan this
                    card prices. */}
                  {/* Book, and beside it a see-through "Learn more" that opens
                    the plan's details, the pair /pricing's plan card uses
                    (Eugene, 30.09). */}
                  <div className="mt-6 grid gap-3 sm:grid-cols-2 sm:gap-4 md:mt-[37px] md:grid-cols-1 lg:grid-cols-2">
                    <BookingCta
                      productId={
                        withMri ? PRODUCT_IDS.core : PRODUCT_IDS.bloodTest
                      }
                      step="product"
                      category={withMri ? "mri" : "blood"}
                      overrideHref={bookingFlowUrl(withMri ? "core" : "blood")}
                      trackingLabel="home_v3_baseline_cta"
                      trackingLocation="dev/home-v4"
                      className="block"
                    >
                      {/* DS `lg` is the 52px height and the 16/24 label; only
                      the colours and the side padding are overridden. */}
                      <Button
                        size="lg"
                        variant="primary"
                        className="background-study-cta w-full bg-white px-6 text-neutral-950 hover:bg-neutral-100 hover:shadow-none"
                      >
                        {BASELINE.cta}
                      </Button>
                    </BookingCta>
                    <Button
                      type="button"
                      size="lg"
                      variant="text"
                      aria-haspopup="dialog"
                      onClick={() => setInfoOpen(true)}
                      className="w-full bg-white/20 px-6 text-white hover:bg-white/30 focus-visible:ring-white/60"
                    >
                      {learnMore}
                    </Button>
                  </div>
                </div>

                {/* The right column starts 14px lower than the left one. */}
                <div className="order-3 md:order-none md:col-start-2 md:row-start-1 md:mt-[14px] md:max-w-[485px]">
                  <ul className="flex gap-3 px-[11px]">
                    {BASELINE.promises.map((p) => {
                      const Icon = PROMISE_ICONS[p.icon]
                      return (
                        <li key={p.text} className="flex-1 py-2">
                          <span className="flex size-10 items-center">
                            {p.icon === "stethoscope" && withMri ? (
                              <Image
                                src="https://wvms6gve4aqx5vj4.public.blob.vercel-storage.com/images/experiments/baseline-backgrounds/anna.webp"
                                alt=""
                                width={40}
                                height={40}
                                className="size-10 rounded-full object-cover ring-1 ring-white/30"
                              />
                            ) : (
                              <Icon className="size-7 text-white" />
                            )}
                          </span>
                          <p className="type-body-md-emphasized mt-1 text-balance text-white">
                            {p.icon === "stethoscope" && withMri
                              ? BASELINE.doctorCall
                              : p.text}
                          </p>
                        </li>
                      )
                    })}
                  </ul>

                  {/* Six rows of space are always reserved (36 + 4 each) even
                  though five are filled: the sixth is the MRI row, and the
                  slack it occupies is the gap that sat above the strip
                  anyway — so the card is the design's 671 either way and the
                  strip never moves. */}
                  {/* The row gap is a margin rather than `gap`, so the closed
                  row can take its own spacing down to nothing with it. Six
                  open rows still come to the design's 236.

                  A row opens to its own height (grid rows 0fr to 1fr), not
                  to a fixed 36: v4's longer lines wrapped inside the 36px
                  box and its overflow cut the second line in half (Eugene,
                  18.09). One line is still the aim; a translation that runs
                  to two now just makes the row taller. */}
                  <ul className="mt-6 flex w-full flex-col md:min-h-[236px] md:max-w-[400px]">
                    {includedRows.map(({ item, open }) => (
                      <li
                        key={item.text}
                        className={cn(
                          "grid transition-[grid-template-rows,margin-bottom,opacity] duration-300 ease-out last:mb-0 motion-reduce:transition-none",
                          open
                            ? "mb-1 grid-rows-[1fr] opacity-100"
                            : "mb-0 grid-rows-[0fr] opacity-0"
                        )}
                        aria-hidden={!open}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <div className="flex min-h-9 items-center gap-3 rounded-[18px] px-[11px] py-1.5">
                            {item.highlight ? (
                              <PercentSealIcon className="text-deep-green-400 size-[22px] shrink-0" />
                            ) : (
                              <CheckCircleNeutralIcon
                                size={22}
                                className="text-deep-green-400 shrink-0"
                              />
                            )}
                            <span className="type-body-lg text-white">
                              {item.text}
                            </span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Figma 328:13496. Named "Layout study, Ahead V2" in the file, so
              treat the arrangement as provisional — but the offer it states
              (the MRI as a one-time add-on to the membership) is real, and the
              figure is read live rather than typed. */}
                {/* The whole strip is the control: Figma 328:13496 draws a switch,
              and a switch you can only look at is worse than none. Clicking
              anywhere on it toggles the add-on, which rewrites the price line
              and the sentence above it. A <button role="switch"> rather than a
              div, so it is reachable and announced. "Learn more" opens the
              scan's popup, as the plan's own does, instead of leaving the
              page (Vega, bugbash 14.10).

              The gaps are all 21px, which is what lands the knob at x=575 of
              the strip — directly under the check icons in the column above,
              as drawn. */}
                {/* The scan to add: the plain full-body MRI, which replaced
                  Advanced here (Eugene, 13.10). */}
                <div className="order-2 flex flex-col gap-2 md:order-none md:col-span-2 md:row-start-2 md:mt-[29px]">
                  {[
                    {
                      id: "core" as const,
                      title: BASELINE.upsell.title,
                      sub: BASELINE.upsell.sub,
                    },
                  ].map((o) => (
                    <div
                      onClick={() => toggle(o.id)}
                      data-on={mri === o.id || undefined}
                      key={o.id}
                      className="hv3-bl-strip flex w-full cursor-pointer flex-wrap items-center gap-3 rounded-[18px] p-4 text-left transition-[background-color,box-shadow] md:min-h-[103px] md:flex-nowrap md:gap-[21px] md:py-2 md:pr-[26px] md:pl-2"
                    >
                      <Image
                        src="https://wvms6gve4aqx5vj4.public.blob.vercel-storage.com/images/experiments/baseline-backgrounds/mri-blue-dark.png"
                        alt=""
                        width={83}
                        height={83}
                        className="size-16 shrink-0 rounded-[12px] bg-[#172d38] object-contain pt-[6px] md:size-[83px]"
                      />
                      <div className="order-4 w-full md:order-2 md:max-w-[540px] md:min-w-0 md:flex-1">
                        <p className="hv3-bl-upsell-title text-white">
                          {o.title}
                        </p>
                        <div className="hv3-bl-sub mt-[5px] flex flex-wrap gap-x-[19px] gap-y-1 text-white/80">
                          <span>{o.sub}</span>
                          <button
                            type="button"
                            aria-haspopup="dialog"
                            onClick={(e) => {
                              e.stopPropagation()
                              setMriInfoOpen(true)
                            }}
                            className="cursor-pointer whitespace-nowrap underline hover:text-white"
                          >
                            {BASELINE.upsell.link}
                          </button>
                        </div>
                      </div>
                      {/* On a phone the strip is two rows — thumbnail and control, then
                  the copy — so the switch and the figure sit in the top-right
                  corner where the result is read. `md:contents` dissolves this
                  wrapper on desktop, putting both back on the strip's own flex
                  line at the Figma offsets. */}
                      <div className="order-2 ml-auto flex flex-col items-end gap-2 md:contents">
                        {/* The switch is the real control — focusable and announced —
                  while the strip around it is a click target for the same
                  action. That split is what lets "Learn more" be its own
                  button: a button nested inside a <button> is invalid HTML. */}
                        <button
                          type="button"
                          role="switch"
                          aria-checked={mri === o.id}
                          aria-label={o.title}
                          onClick={(e) => {
                            e.stopPropagation()
                            toggle(o.id)
                          }}
                          className={cn(
                            "hv3-bl-mri-switch relative h-8 w-14 shrink-0 cursor-pointer rounded-full transition-colors md:order-3",
                            mri === o.id ? "bg-deep-green-400" : "bg-white/25"
                          )}
                        >
                          <span
                            className={cn(
                              "hv3-bl-knob absolute top-1 size-6 rounded-[12px] transition-[left]",
                              mri === o.id ? "left-[28px]" : "left-1"
                            )}
                          />
                        </button>
                        <p className="shrink-0 text-right whitespace-nowrap text-white md:order-4 md:text-left">
                          <span className="hv3-bl-upsell-price">
                            {onTop(o.id)
                              ? `+${formatCurrency(onTop(o.id), country)}`
                              : ""}
                          </span>
                          <span className="hv3-bl-upsell-suffix">
                            {BASELINE.upsell.priceSuffix}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Under the card, as on /pricing: cancelling, and the one quiet way
            out to a one-time check-up (Eugene, bugbash 14.10). */}
        <div
          className={cn(
            section,
            "type-body-md mt-6 flex flex-col items-center gap-1 text-center sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-3"
          )}
        >
          <p className="text-pretty text-neutral-600">{PLAN.cancelNote}</p>
          <OneTimeLink
            trackingLabel="home_v4_baseline_one_off"
            trackingLocation="home_v4_baseline"
            className="text-neutral-900 underline decoration-neutral-900/40 underline-offset-2 transition-colors hover:decoration-neutral-900"
          >
            {PLAN.oneOff}
          </OneTimeLink>
        </div>
      </section>
      <PlanInfoSheet open={infoOpen} onClose={() => setInfoOpen(false)} />
      <MriInfoSheet
        id="core"
        open={mriInfoOpen}
        onClose={() => setMriInfoOpen(false)}
      />
    </PlanPricesProvider>
  )
}
